import axiosInstance from "../Axios";

// Target statuses for PATCH /grievance/:id/status (same numbers as the
// backend's GRIEVANCE_STATUS): the status says which action is meant.
const GRIEVANCE_STATUS_VALUES = {
  UNDER_REVIEW: 2, // approve
  CLOSED: 8, // accept the resolution
  REJECTED: 9, // reject
  REOPENED: 10,
  WITHDRAWN: 12,
};

function raiseGrievance(formData) {
  try {
    return axiosInstance.post("/grievance", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (error) {
    console.log("Error while raising grievance:", error);
    throw error;
  }
}

// Grievances can't be edited or deleted — the employee who raised one
// withdraws it instead. reason is optional and shows in the activity log.
function withdrawGrievance(grievanceId, reason) {
  try {
    return axiosInstance.patch(`/grievance/${grievanceId}/status`, {
      status: GRIEVANCE_STATUS_VALUES.WITHDRAWN,
      reason: reason?.trim() || undefined,
    });
  } catch (error) {
    console.log("Error while withdrawing grievance:", error);
    throw error;
  }
}

// The employee reopens a Resolved/Closed grievance. reason is optional and
// shows in the activity log.
function reopenGrievance(grievanceId, reason) {
  try {
    return axiosInstance.patch(`/grievance/${grievanceId}/status`, {
      status: GRIEVANCE_STATUS_VALUES.REOPENED,
      reason: reason?.trim() || undefined,
    });
  } catch (error) {
    console.log("Error while reopening grievance:", error);
    throw error;
  }
}

// The employee accepts HR's resolution, which closes the case. comment is
// optional and shows in the activity log.
function acceptGrievanceResolution(grievanceId, comment) {
  try {
    return axiosInstance.patch(`/grievance/${grievanceId}/status`, {
      status: GRIEVANCE_STATUS_VALUES.CLOSED,
      reason: comment?.trim() || undefined,
    });
  } catch (error) {
    console.log("Error while accepting grievance resolution:", error);
    throw error;
  }
}

function getMyGrievances(pageNo = 1, perPage = 20, filters = {}) {
  try {
    return axiosInstance.get("/grievance", {
      params: { pageNo, perPage, ...filters },
    });
  } catch (error) {
    console.log("Error while fetching grievances:", error);
    throw error;
  }
}

function getGrievanceDetails(grievanceId) {
  try {
    return axiosInstance.get(`/grievance/${grievanceId}`);
  } catch (error) {
    console.log("Error while fetching grievance details:", error);
    throw error;
  }
}

function getGrievanceCategories() {
  try {
    return axiosInstance.get("/grievance-categories");
  } catch (error) {
    console.log("Error while fetching grievance categories:", error);
    throw error;
  }
}

function getGrievanceSubcategories(categoryId) {
  try {
    return axiosInstance.get(
      `/grievance-categories/${categoryId}/subcategories`
    );
  } catch (error) {
    console.log("Error while fetching grievance subcategories:", error);
    throw error;
  }
}

// Admin/HR-only management view (active + inactive, nested) — Grievance
// Setting page. Same endpoint as getGrievanceCategories, switched via ?manage.
function getGrievanceCategoryManageList() {
  try {
    return axiosInstance.get("/grievance-categories", {
      params: { manage: true },
    });
  } catch (error) {
    console.log("Error while fetching grievance category list:", error);
    throw error;
  }
}

// payload: { name, parent_id? } — omit/null parent_id for a top-level category.
function createGrievanceCategory(payload) {
  try {
    return axiosInstance.post("/grievance-categories", payload);
  } catch (error) {
    console.log("Error while creating grievance category:", error);
    throw error;
  }
}

function updateGrievanceCategory(categoryId, payload) {
  try {
    return axiosInstance.put(`/grievance-categories/${categoryId}`, payload);
  } catch (error) {
    console.log("Error while updating grievance category:", error);
    throw error;
  }
}

function deleteGrievanceCategory(categoryId) {
  try {
    return axiosInstance.delete(`/grievance-categories/${categoryId}`);
  } catch (error) {
    console.log("Error while deleting grievance category:", error);
    throw error;
  }
}

function getGrievanceActivity(grievanceId, pageNo = 1, perPage = 10) {
  try {
    return axiosInstance.get(`/grievance/${grievanceId}/activity`, {
      params: { pageNo, perPage },
    });
  } catch (error) {
    console.log("Error while fetching grievance activity:", error);
    throw error;
  }
}

function addGrievanceComment(grievanceId, formData) {
  try {
    return axiosInstance.post(`/grievance/${grievanceId}/comments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (error) {
    console.log("Error while adding grievance comment:", error);
    throw error;
  }
}

function downloadGrievanceDocument(fileName) {
  try {
    return axiosInstance.get("/grievance/download", {
      params: { fileName },
      responseType: "blob",
    });
  } catch (error) {
    console.log("Error while downloading document:", error);
    throw error;
  }
}

// Investigate modal's Assignee dropdown, filtered to whichever role was picked.
function getUsersByRole(roleId) {
  try {
    return axiosInstance.get("/grievance/assignable-users", {
      params: { roleId },
    });
  } catch (error) {
    console.log("Error while fetching assignable users:", error);
    throw error;
  }
}

// Investigate modal's Role dropdown.
function getGrievanceRoles() {
  try {
    return axiosInstance.get("/grievance/roles");
  } catch (error) {
    console.log("Error while fetching roles:", error);
    throw error;
  }
}

// payload: { reason } — required for reject, optional for approve
function approveRejectGrievance(grievanceId, action, payload = {}) {
  try {
    return axiosInstance.patch(`/grievance/${grievanceId}/status`, {
      status:
        action === "approve"
          ? GRIEVANCE_STATUS_VALUES.UNDER_REVIEW
          : GRIEVANCE_STATUS_VALUES.REJECTED,
      ...payload,
    });
  } catch (error) {
    console.log("Error while approving/rejecting grievance:", error);
    throw error;
  }
}

// payload: { priority, assigned_to }
function investigateGrievance(grievanceId, payload) {
  try {
    return axiosInstance.patch(
      `/grievance/${grievanceId}/investigate`,
      payload
    );
  } catch (error) {
    console.log("Error while assigning grievance for investigation:", error);
    throw error;
  }
}

// formData: findings, resolution_note, documents? (optional attachment)
function resolveGrievance(grievanceId, formData) {
  try {
    return axiosInstance.patch(`/grievance/${grievanceId}/resolve`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (error) {
    console.log("Error while resolving grievance:", error);
    throw error;
  }
}

// Investigation workspace's "Add Person" picker.
function searchUsers(query) {
  try {
    return axiosInstance.get("/grievance/search-users", {
      params: { query },
    });
  } catch (error) {
    console.log("Error while searching users:", error);
    throw error;
  }
}

function addParticipant(grievanceId, participantId) {
  try {
    return axiosInstance.post(`/grievance/${grievanceId}/participants`, {
      participant_id: participantId,
    });
  } catch (error) {
    console.log("Error while adding grievance participant:", error);
    throw error;
  }
}

function getParticipants(grievanceId) {
  try {
    return axiosInstance.get(`/grievance/${grievanceId}/participants`);
  } catch (error) {
    console.log("Error while fetching grievance participants:", error);
    throw error;
  }
}

// HR Manager/Admin reports page. params: { from_date?, to_date? } (YYYY-MM-DD).
function getGrievanceReport(params = {}) {
  try {
    return axiosInstance.get("/grievance/reports/summary", { params });
  } catch (error) {
    console.log("Error while fetching grievance report:", error);
    throw error;
  }
}

export {
  raiseGrievance,
  acceptGrievanceResolution,
  getGrievanceReport,
  withdrawGrievance,
  reopenGrievance,
  getMyGrievances,
  getGrievanceDetails,
  getGrievanceCategories,
  getGrievanceSubcategories,
  getGrievanceCategoryManageList,
  createGrievanceCategory,
  updateGrievanceCategory,
  deleteGrievanceCategory,
  getUsersByRole,
  getGrievanceRoles,
  approveRejectGrievance,
  investigateGrievance,
  resolveGrievance,
  getGrievanceActivity,
  addGrievanceComment,
  downloadGrievanceDocument,
  searchUsers,
  addParticipant,
  getParticipants,
};
